import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Wrench, SealCheck, MapPin, ArrowRight, EnvelopeSimple } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Sidebar } from "@/components/Sidebar";
import { Button } from "@/components/ui/button";

export default function ATraiter() {
  const navigate = useNavigate();
  const [data, setData] = useState({ revision_requested: [], validated: [] });
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/dossiers/summary");
      setData(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const sendDigest = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/dossiers/send-digest");
      toast.success(`Récapitulatif envoyé à ${data.sent_to}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const Card = ({ d, accent }) => (
    <div data-testid={`todo-item-${d.id}`} onClick={() => navigate(`/admin/dossier/${d.id}`)}
      className="bg-white border border-slate-200 rounded-md p-5 hover:border-blue-400 hover:shadow-sm cursor-pointer transition-all flex items-center justify-between gap-4">
      <div className="min-w-0">
        <p className="font-medium text-slate-950">{d.client_name}</p>
        <div className="flex items-center gap-1.5 text-slate-500 text-sm mt-0.5">
          <MapPin size={14} className="shrink-0" /> <span className="truncate">{d.property_address}</span>
        </div>
        {accent === "rev" && d.remarks?.length > 0 && (
          <p className="text-sm text-orange-700 bg-orange-50 border border-orange-100 rounded px-2.5 py-1.5 mt-2">
            « {d.remarks[d.remarks.length - 1].text} »
          </p>
        )}
      </div>
      <ArrowRight size={18} className="text-slate-400 shrink-0" />
    </div>
  );

  const Section = ({ title, subtitle, icon: Icon, color, items, accent, testid }) => (
    <div data-testid={testid}>
      <div className="flex items-center gap-2.5 mb-1">
        <div className={`w-9 h-9 rounded-md flex items-center justify-center ${color.bg}`}>
          <Icon size={20} weight="fill" className={color.text} />
        </div>
        <h2 className="font-heading text-xl font-medium text-slate-900">{title}</h2>
        <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-semibold ${color.badge}`}>{items.length}</span>
      </div>
      <p className="text-sm text-slate-500 mb-4 ml-11">{subtitle}</p>
      <div className="space-y-3">
        {items.length === 0 ? (
          <div className="text-sm text-slate-400 bg-white border border-dashed border-slate-200 rounded-md p-6 text-center">
            Rien à traiter ici pour le moment. 🎉
          </div>
        ) : items.map((d) => <Card key={d.id} d={d} accent={accent} />)}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      <main className="ml-64 p-8 max-w-4xl" data-testid="a-traiter-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="label-eyebrow mb-1">Suivi quotidien</p>
            <h1 className="font-heading text-3xl font-medium tracking-tight text-slate-950">À traiter</h1>
          </div>
          <Button variant="outline" data-testid="send-digest-button" onClick={sendDigest} disabled={sending} className="h-11">
            <EnvelopeSimple size={18} className="mr-1.5" />
            {sending ? "Envoi…" : "M'envoyer le récap maintenant"}
          </Button>
        </div>

        <div className="rounded-md bg-blue-50 border border-blue-100 p-4 text-sm text-blue-800 mb-8">
          Un récapitulatif de ces deux listes vous est envoyé automatiquement par email chaque jour à <strong>05:00</strong>.
        </div>

        {loading ? (
          <p className="text-slate-400">Chargement…</p>
        ) : (
          <div className="space-y-10">
            <Section testid="section-revision" title="Rapports à corriger" accent="rev"
              subtitle="Le client a téléchargé le rapport et demande une modification."
              icon={Wrench} items={data.revision_requested}
              color={{ bg: "bg-orange-100", text: "text-orange-600", badge: "bg-orange-100 text-orange-700" }} />
            <Section testid="section-validation" title="Rapports à valider définitivement" accent="val"
              subtitle="Le client a validé — postez le rapport définitif."
              icon={SealCheck} items={data.validated}
              color={{ bg: "bg-violet-100", text: "text-violet-600", badge: "bg-violet-100 text-violet-700" }} />
          </div>
        )}
      </main>
    </div>
  );
}
